// src/components/institution/manager/HeroSectionTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  School, 
  Users, 
  Award, 
  Calendar,
  Eye,
  Plus,
  Trash2,
  Settings,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';

export const HeroSectionTab = ({ bookId, institutionData, onUpdate }) => {
  const [heroData, setHeroData] = useState({
    institutionName: '',
    tagline: '',
    description: '',
    showQuickStats: true,
    studentCount: '',
    establishedYear: '',
    actionButtons: [
      { text: 'Explore Programs', action: 'programs', style: 'primary' },
      { text: 'Apply Now', action: 'admissions', style: 'secondary' }
    ],
    customStats: []
  });

  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Get existing images from institutionData
  const existingLogo = institutionData?.logo || institutionData?.logoUrl;
  const existingHeroImage = institutionData?.heroImage || institutionData?.coverImageUrl || institutionData?.backgroundImage;

  useEffect(() => {
    if (institutionData) {
      setHeroData({
        institutionName: institutionData.botName || '',
        tagline: institutionData.institutionSettings?.tagline || '',
        description: institutionData.description || '',
        showQuickStats: institutionData.institutionSettings?.showQuickStats ?? true,
        studentCount: institutionData.institutionSettings?.studentCount || '',
        establishedYear: institutionData.institutionSettings?.establishedYear || '',
        actionButtons: institutionData.institutionSettings?.actionButtons || [
          { text: 'Explore Programs', action: 'programs', style: 'primary' },
          { text: 'Apply Now', action: 'admissions', style: 'secondary' }
        ],
        customStats: institutionData.institutionSettings?.customStats || []
      });
    }
  }, [institutionData]);

  const handleInputChange = (field, value) => {
    setHeroData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedData = {
        botName: heroData.institutionName,
        description: heroData.description,
        // Don't update logo/heroImage here - they should only be managed in Settings
        institutionSettings: {
          ...institutionData?.institutionSettings,
          tagline: heroData.tagline,
          showQuickStats: heroData.showQuickStats,
          studentCount: heroData.studentCount,
          establishedYear: heroData.establishedYear,
          actionButtons: heroData.actionButtons,
          customStats: heroData.customStats
        }
      };

      await institutionService.updateInstitution(bookId, updatedData);
      await onUpdate();
      alert('Hero section updated successfully!');
    } catch (error) {
      console.error('Error updating hero section:', error);
      alert('Failed to update hero section');
    } finally {
      setSaving(false);
    }
  };

  const addCustomStat = () => {
    setHeroData(prev => ({
      ...prev,
      customStats: [...prev.customStats, { label: '', value: '', icon: 'Users' }]
    }));
  };

  const updateCustomStat = (index, field, value) => {
    setHeroData(prev => ({
      ...prev,
      customStats: prev.customStats.map((stat, i) => 
        i === index ? { ...stat, [field]: value } : stat
      )
    }));
  };

  const removeCustomStat = (index) => {
    setHeroData(prev => ({
      ...prev,
      customStats: prev.customStats.filter((_, i) => i !== index)
    }));
  };

  const addActionButton = () => {
    setHeroData(prev => ({
      ...prev,
      actionButtons: [...prev.actionButtons, { text: '', action: 'programs', style: 'primary' }]
    }));
  };

  const updateActionButton = (index, field, value) => {
    setHeroData(prev => ({
      ...prev,
      actionButtons: prev.actionButtons.map((button, i) => 
        i === index ? { ...button, [field]: value } : button
      )
    }));
  };

  const removeActionButton = (index) => {
    setHeroData(prev => ({
      ...prev,
      actionButtons: prev.actionButtons.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Hero Section Configuration</h2>
          <p className="text-gray-600 mt-2">Configure your institution's homepage hero section</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="h-4 w-4" />
            {previewMode ? 'Edit' : 'Preview'}
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {previewMode ? (
        /* Preview Mode */
        <div className="bg-white rounded-xl shadow-sm border p-8">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-4 text-gray-500">Hero Section Preview</h3>
            <div 
              className="relative min-h-[400px] rounded-lg flex items-center justify-center text-white p-8"
              style={{
                background: existingHeroImage 
                  ? `linear-gradient(rgba(30, 64, 175, 0.8), rgba(30, 64, 175, 0.8)), url(${existingHeroImage})`
                  : 'linear-gradient(135deg, #1e40af, #3b82f6)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="text-center max-w-4xl">
                {existingLogo && (
                  <img 
                    src={existingLogo} 
                    alt="Logo" 
                    className="h-20 w-20 mx-auto rounded-full object-cover mb-6 border-4 border-white/20"
                  />
                )}
                
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  {heroData.institutionName || 'Institution Name'}
                </h1>
                
                {heroData.tagline && (
                  <p className="text-xl text-blue-100 mb-6">
                    {heroData.tagline}
                  </p>
                )}
                
                {heroData.description && (
                  <p className="text-lg text-blue-50 mb-8 max-w-2xl mx-auto">
                    {heroData.description}
                  </p>
                )}
                
                <div className="flex gap-4 justify-center mb-8">
                  {heroData.actionButtons.map((button, index) => (
                    <button
                      key={index}
                      className={`px-6 py-3 rounded-lg font-semibold ${
                        button.style === 'primary' 
                          ? 'bg-white text-blue-900 hover:bg-blue-50' 
                          : 'border-2 border-white text-white hover:bg-white hover:text-blue-900'
                      } transition-colors`}
                    >
                      {button.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Edit Mode */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Basic Information */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <School className="h-5 w-5 text-indigo-600" />
                Basic Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Institution Name *
                  </label>
                  <input
                    type="text"
                    value={heroData.institutionName}
                    onChange={(e) => handleInputChange('institutionName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter institution name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tagline
                  </label>
                  <input
                    type="text"
                    value={heroData.tagline}
                    onChange={(e) => handleInputChange('tagline', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., Excellence in Education"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={heroData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Brief description of your institution"
                  />
                </div>
              </div>
            </div>

            {/* Images - Reference Only */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-indigo-600" />
                Images
              </h3>
              
              <div className="space-y-6">
                {/* Current Logo */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Institution Logo
                    </label>
                    <button
                      onClick={() => window.open(`/books/${bookId}/edit`, '_blank')}
                      className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                    >
                      <Settings className="h-3 w-3" />
                      Update in Settings
                    </button>
                  </div>
                  
                  {existingLogo ? (
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <img
                        src={existingLogo}
                        alt="Institution logo"
                        className="w-12 h-12 object-cover rounded-full"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">Logo uploaded</p>
                        <p className="text-xs text-gray-500">Manage in Institution Settings</p>
                      </div>
                      <div className="text-green-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg bg-yellow-50">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        <School className="h-6 w-6 text-gray-400" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">No logo uploaded</p>
                        <p className="text-xs text-gray-500">Upload in Institution Settings</p>
                      </div>
                      <AlertCircle className="w-5 h-5 text-yellow-600" />
                    </div>
                  )}
                </div>

                {/* Current Hero Image */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      Hero Background Image
                    </label>
                    <button
                      onClick={() => window.open(`/books/${bookId}/edit`, '_blank')}
                      className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                    >
                      <Settings className="h-3 w-3" />
                      Update in Settings
                    </button>
                  </div>
                  
                  {existingHeroImage ? (
                    <div className="relative">
                      <img
                        src={existingHeroImage}
                        alt="Hero background"
                        className="w-full h-24 object-cover rounded-lg"
                      />
                      <div className="absolute top-2 right-2 bg-green-600 text-white px-2 py-1 rounded text-xs">
                        ✓ Uploaded
                      </div>
                    </div>
                  ) : (
                    <div className="w-full h-24 bg-gray-200 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                      <div className="text-center">
                        <AlertCircle className="h-6 w-6 text-yellow-600 mx-auto mb-1" />
                        <p className="text-xs text-gray-500">No hero image uploaded</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <ExternalLink className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Manage Images</p>
                      <p className="text-xs text-blue-700 mt-1">
                        To upload or change the logo and hero image, use the Institution Settings. 
                        This prevents duplicate uploads and keeps your images organized.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Statistics and Actions - Keep existing code */}
          <div className="space-y-6">
            {/* Quick Stats Configuration */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Award className="h-5 w-5 text-indigo-600" />
                  Quick Statistics
                </h3>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={heroData.showQuickStats}
                    onChange={(e) => handleInputChange('showQuickStats', e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">Show Stats</span>
                </label>
              </div>

              {heroData.showQuickStats && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Student Count
                      </label>
                      <input
                        type="text"
                        value={heroData.studentCount}
                        onChange={(e) => handleInputChange('studentCount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., 500+"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Established Year
                      </label>
                      <input
                        type="number"
                        value={heroData.establishedYear}
                        onChange={(e) => handleInputChange('establishedYear', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g., 1990"
                      />
                    </div>
                  </div>

                  {/* Custom Stats */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Custom Statistics</label>
                      <button
                        onClick={addCustomStat}
                        className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                      >
                        <Plus className="h-3 w-3" />
                        Add Stat
                      </button>
                    </div>

                    {heroData.customStats.map((stat, index) => (
                      <div key={index} className="flex gap-2 mb-2 p-3 border border-gray-200 rounded-lg">
                        <input
                          type="text"
                          value={stat.label}
                          onChange={(e) => updateCustomStat(index, 'label', e.target.value)}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Label"
                        />
                        <input
                          type="text"
                          value={stat.value}
                          onChange={(e) => updateCustomStat(index, 'value', e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="Value"
                        />
                        <button
                          onClick={() => removeCustomStat(index)}
                          className="p-1 text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons Configuration */}
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Action Buttons</h3>
                <button
                  onClick={addActionButton}
                  className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Button
                </button>
              </div>

              <div className="space-y-3">
                {heroData.actionButtons.map((button, index) => (
                  <div key={index} className="flex gap-2 p-3 border border-gray-200 rounded-lg">
                    <input
                      type="text"
                      value={button.text}
                      onChange={(e) => updateActionButton(index, 'text', e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Button text"
                    />
                    <select
                      value={button.action}
                      onChange={(e) => updateActionButton(index, 'action', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="programs">Programs</option>
                      <option value="admissions">Admissions</option>
                      <option value="contact">Contact</option>
                      <option value="about">About</option>
                    </select>
                    <select
                      value={button.style}
                      onChange={(e) => updateActionButton(index, 'style', e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    >
                      <option value="primary">Primary</option>
                      <option value="secondary">Secondary</option>
                    </select>
                    <button
                      onClick={() => removeActionButton(index)}
                      className="p-1 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};